import { Component, OnDestroy, OnInit } from '@angular/core';
import { IUserRequest } from '../../../core/interfaces/users';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserApiService } from '../../../core/services/user-api.service';
import { Router } from '@angular/router';
import { CustomDatePipe } from '../../../core/pipes/custom-date.pipe';
import { BrowserCheckService } from '../../../core/services/browser-check.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CompanyCodeService } from '../../../core/services/adminService/company-code.service';
import { CommonService } from '../../../core/services/common.service';
import { constantTxt } from '../../../core/constants/constants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-manage-request-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomDatePipe,
    NzButtonModule,
    NzSelectModule,
    NzInputModule,
    NzTableModule,
    NzIconModule,
  ],
  templateUrl: './user-manage-request-form.component.html',
  styleUrls: ['./user-manage-request-form.component.less'],
})
export class UserManageRequestFormComponent implements OnInit, OnDestroy {
  isViewMode: boolean = false;
  manageForm!: FormGroup;
  requests: IUserRequest[] = [];
  companies: any[] = [];
  buyers: any[] = [];
  purchasingOffices: any[] = [];
  statuses = [
    'ALL',
    'DRAFT',
    'OPEN',
    'IN PROGRESS',
    'PARTIAL QUOTED',
    'CLOSED',
    'CANCELED',
  ];
  itemNo: number = 1;
  userFormAddItemService: any;
  attachmentMap: { [key: string]: boolean } = {};
  generatedPnaIdTemp: any[] = [];
  userEmail: string = '';
  pageSize = 100;
  pageNumber = 1;
  selectedRequests: Set<number> = new Set(); // Store selected request IDs
  customNoDataMessage = constantTxt.noDataFound;

  companiesSubscription!: Subscription;
  getOfficesListSubscription!: Subscription;
  getBuyersSubscription!: Subscription;
  deletePnaRequestsSubscription!: Subscription;
  searchRequestsSubscription!: Subscription;
  createPnaIdTempSubscription!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userApi: UserApiService,
    private readonly router: Router,
    private readonly browserCheck: BrowserCheckService,
    private readonly companyCodeService: CompanyCodeService,
    private readonly commonService: CommonService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadInitialData();
    this.loadRecords();
  }

  initializeForm() {
    this.manageForm = this.fb.group({
      companyCode: ['', Validators.required],
      buyer: ['', Validators.required],
      purchasingOffice: ['', Validators.required],
      status: ['DRAFT'],
      pnaNo: ['', [Validators.pattern('^\\d+(?:-\\d+)?$')]],
    });
  }

  loadInitialData(): void {
    this.companiesSubscription = this.companyCodeService
      .getCompanies()
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.companies = data;
          }
        },
        error: (error) => {
          this.companies = [];
          console.log('Error occured in fetching companies. Error :', error);
        },
      });

    this.getBuyersSubscription = this.userApi.getBuyers().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.buyers = data;
        }
      },
      error: (error) => {
        this.buyers = [];
        console.log('Error occured in fetching buyers. Error :', error);
      },
    });

    this.getOfficesListSubscription = this.userApi.getOfficesList().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.purchasingOffices = data;
        }
      },
      error: (error) => {
        this.purchasingOffices = [];
        console.log('Error occured in fetching offices. Error :', error);
      },
    });
  }

  loadRecords(): void {
    const pnaNoControl = this.manageForm.get('pnaNo');
    const pnaNoValue = pnaNoControl?.value?.trim();
    pnaNoControl?.markAsTouched();
    if (pnaNoValue && pnaNoControl?.invalid) {
      return;
    }

    const filter = this.manageForm.value;
    const requestDto: any = {};
    if (filter.companyCode) requestDto.ccn = filter.companyCode;
    if (filter.buyer) requestDto.buyer = filter.buyer;
    if (filter.purchasingOffice)
      requestDto.purchasing = filter.purchasingOffice;
    if (filter.status) requestDto.status = filter.status;
    if (filter.pnaNo) {
      const { pnaNo } = this.commonService.extractPNA(filter.pnaNo || '');
      requestDto.pnaNo = pnaNo;
    }

    this.searchRequestsSubscription = this.userApi
      .searchRequests(requestDto)
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            const requestItems = data.map((data: IUserRequest) => ({
              pnaNo: data.pnaNo,
              pnaCcn: data.pnaCcn,
              pnaPurchasing: data.pnaPurchasing,
              pnaBuyerFullName: data.pnaBuyerFullName,
              pnaRequestorFullName: data.pnaRequestorFullName,
              pnaStatus: data.pnaStatus,
              pnaCreateDate: data.pnaCreateDate,
              pnaUpdateDate: data.pnaUpdateDate,
              pnaId: data.pnaId,
              pnaYear: data.pnaYear,
              hasAttachment: data.hasAttachment,
            }));

            this.requests = requestItems;
          } else {
            this.requests = [];
          }
        },
        error: (error) => {
          this.requests = [];
          console.log(
            'Error occured while fetching user manage requests. Error:',
            error
          );
        },
      });
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageNumber = 1;
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageNumber = pageIndex;
  }

  onAddNewRequest(): void {
    this.userEmail = this.browserCheck.getSessionStorage('UserEmail') || '';

    this.createPnaIdTempSubscription = this.userApi
      .createPnaIdTemp(this.userEmail)
      .subscribe({
        next: (response) => {
          const data = response.body;
          if (data) {
            // Prepare navigation state data (hidden from URL)
            const navigationState = {
              mode: 'add',
              pnaIdTemp: data,
              role: 'user',
              returnUrl: '/user/request',
            };

            this.router.navigate(['/user/add-request'], {
              state: navigationState,
            });
          }
        },
        error: (err) => {
          console.error('Failed to generate PnaIdTemp:', err);
          this.generatedPnaIdTemp = [];
        },
      });
  }

  // Navigate to view request with clean URL - all data passed through state
  navigateToView(pnaNo: string, pnaYear: string): void {
    const navigationState = {
      mode: 'view',
      role: 'user',
      pnaNo: pnaNo,
      year: pnaYear,
      returnUrl: '/user/request',
    };

    this.router.navigate(['/user/view-request'], {
      state: navigationState,
    });
  }

  // Navigate to edit request with clean URL - all data passed through state
  navigateToEdit(pnaNo: string, pnaYear: string): void {
    const navigationState = {
      mode: 'edit',
      role: 'user',
      pnaNo: pnaNo,
      year: pnaYear,
      returnUrl: '/user/request',
    };

    this.router.navigate(['/user/edit-request'], {
      state: navigationState,
    });
  }

  onDelete() {
    const ids = Array.from(this.selectedRequests);
    if (ids.length === 0) {
      alert('Please select at least one request to delete.');
      return;
    }

    if (confirm('Are you sure you want to delete the selected request(s)?')) {
      this.deletePnaRequestsSubscription = this.userApi
        .deletePnaRequests(ids)
        .subscribe({
          next: () => {
            alert('Request(s) deleted successfully.');
            this.selectedRequests.clear();
            this.loadRecords();
          },
          error: (err) => {
            console.error('Error deleting requests:', err);
          },
        });
    }
  }

  toggleSelection(requestId: number): void {
    if (this.selectedRequests.has(requestId)) {
      this.selectedRequests.delete(requestId);
    } else {
      this.selectedRequests.add(requestId);
    }
  }

  getStatusFlags(status: string | undefined | null) {
    const s = (status || '').toLowerCase();
    return {
      isDraft: s === 'draft',
      isOpen: s === 'open',
      isDeleted: s === 'deleted',
      isClosed: s === 'closed',
      inProgress: s === 'in progress',
      isPartiallyQuoted: s === 'partial quoted',
    };
  }

  ngOnDestroy(): void {
    this.companiesSubscription?.unsubscribe();
    this.getOfficesListSubscription?.unsubscribe();
    this.getBuyersSubscription?.unsubscribe();
    this.deletePnaRequestsSubscription?.unsubscribe();
    this.searchRequestsSubscription?.unsubscribe();
    this.createPnaIdTempSubscription?.unsubscribe();
  }
}
